import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Search, Check, Undo2, Pencil } from 'lucide-react';
import { useAppColors } from '@/src/lib/theme';

// Quick edit for a PDF (e.g. a menu): change a price or a few words on the real PDF, keeping its layout.
// The old text is covered with the background colour and the new text is written in the same place and size.
// Good for prices and small wording changes; anything that moves the layout still needs the designer.

type Item = { page: number; idx: number; str: string; x: number; y: number; w: number; h: number; family: 'sans' | 'serif' | 'mono'; bold: boolean };
type PageInfo = { canvas: HTMLCanvasElement; scale: number; dpr: number; heightPt: number; items: Item[] };
type Rgb = [number, number, number];
type Edit = { item: Item; text: string; bg: Rgb; fg: Rgb };

const keyOf = (i: Item) => `${i.page}:${i.idx}`;
const isNumberish = (s: string) => /^[\s$€£]*\d[\d.,]*\s*(pp|pp\.)?\s*$/i.test(s.trim());

export type PdfChange = { find: string; replace: string; near?: string };

export default function PdfQuickEdit({ url, name, onClose, onSave, preset }: {
  url: string; name: string; onClose: () => void;
  /** Changes asked for in chat ("make the chicken $100"), applied for the person to check before saving. */
  preset?: PdfChange[] | null;
  /** mode 'copy' saves next to the original, 'replace' swaps the original. Returns an error message or null. */
  onSave: (file: File, mode: 'copy' | 'replace') => Promise<string | null>;
}) {
  const c = useAppColors();
  const host = useRef<HTMLDivElement>(null);
  const bytes = useRef<ArrayBuffer | null>(null);
  const [pages, setPages] = useState<PageInfo[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [edits, setEdits] = useState<Record<string, Edit>>({});
  const [sel, setSel] = useState<Item | null>(null);
  const [draft, setDraft] = useState('');
  const [find, setFind] = useState('');
  const [repl, setRepl] = useState('');
  const [saving, setSaving] = useState<null | 'copy' | 'replace'>(null);
  const [err, setErr] = useState<string | null>(null);
  const [width, setWidth] = useState(800);
  const [presetNote, setPresetNote] = useState<string | null>(null);
  const [failWhy, setFailWhy] = useState<string | null>(null);

  useEffect(() => {
    let off = false;
    void (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Couldn't download the PDF (${res.status}).`);
        const buf = await res.arrayBuffer();
        bytes.current = buf;
        const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
        pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString();
        const pdf = await pdfjs.getDocument({ data: new Uint8Array(buf.slice(0)) }).promise;
        // The popup may still be opening (0 wide) on a phone: fall back to the screen width.
        const hostW = host.current?.clientWidth ?? 0;
        const w = Math.max(240, Math.min(900, (hostW > 40 ? hostW : window.innerWidth) - 8));
        setWidth(w);
        // iPhones cap how much canvas memory a page can use: keep the resolution sensible there.
        const native = document.documentElement.classList.contains('cap-native');
        const dpr = Math.min(window.devicePixelRatio || 1, native ? 2 : 2.5);
        const out: PageInfo[] = [];
        for (let p = 1; p <= Math.min(pdf.numPages, 12); p++) {
          const page = await pdf.getPage(p);
          const base = page.getViewport({ scale: 1 });
          const scale = w / base.width;
          const vp = page.getViewport({ scale: scale * dpr });
          const canvas = document.createElement('canvas');
          canvas.width = Math.floor(vp.width); canvas.height = Math.floor(vp.height);
          await page.render({ canvas, canvasContext: canvas.getContext('2d', { willReadFrequently: true })!, viewport: vp } as never).promise;
          const tc = await readText(page);
          const items: Item[] = [];
          (tc.items as Array<{ str?: string; transform: number[]; width: number; height: number; fontName: string }>).forEach((it, idx) => {
            if (!it.str || !it.str.trim()) return;
            const [a, b, , , e, f] = it.transform;
            const size = Math.hypot(a, b) || it.height || 10;
            const style = (tc.styles as Record<string, { fontFamily?: string }>)[it.fontName];
            let fontName = '';
            try { fontName = (page.commonObjs.get(it.fontName) as { name?: string } | undefined)?.name ?? ''; } catch { /* not loaded */ }
            const fam = /mono/i.test(style?.fontFamily ?? '') ? 'mono' : /serif/i.test(style?.fontFamily ?? '') && !/sans/i.test(style?.fontFamily ?? '') ? 'serif' : 'sans';
            items.push({ page: p - 1, idx, str: it.str, x: e, y: f, w: it.width, h: size, family: fam, bold: /bold|black|heavy|semibold|demi/i.test(fontName) });
          });
          out.push({ canvas, scale, dpr, heightPt: base.height, items });
          if (off) return;
        }
        if (!off) { setPages(out); setState('ready'); }
      } catch (e) { console.warn('[quick edit]', e); if (!off) { setFailWhy(e instanceof Error ? e.message : String(e)); setState('failed'); } }
    })();
    return () => { off = true; };
  }, [url]);

  // Colours around and inside a piece of text, read from the rendered page.
  const sample = (pg: PageInfo, it: Item): { bg: Rgb; fg: Rgb } => {
    const ctx = pg.canvas.getContext('2d', { willReadFrequently: true })!;
    const k = pg.scale * pg.dpr;
    const left = Math.max(0, Math.floor(it.x * k)), top = Math.max(0, Math.floor((pg.heightPt - it.y - it.h * 0.85) * k));
    const wid = Math.max(1, Math.ceil(it.w * k)), hei = Math.max(1, Math.ceil(it.h * 1.05 * k));
    const px = (x: number, y: number): Rgb => { const d = ctx.getImageData(Math.min(pg.canvas.width - 1, Math.max(0, x)), Math.min(pg.canvas.height - 1, Math.max(0, y)), 1, 1).data; return [d[0], d[1], d[2]]; };
    const around = [px(left - 3, top + hei / 2), px(left + wid + 3, top + hei / 2), px(left + wid / 2, top - 3), px(left + 1, top - 2)];
    const lum = (q: Rgb) => q[0] * 0.3 + q[1] * 0.59 + q[2] * 0.11;
    const bg = around.sort((a, b) => lum(b) - lum(a))[1]; // second lightest: ignores a stray mark
    const inside = ctx.getImageData(left, top, Math.min(wid, pg.canvas.width - left), Math.min(hei, pg.canvas.height - top)).data;
    let best: Rgb = [0, 0, 0], diff = -1;
    for (let i = 0; i < inside.length; i += 16) {
      const q: Rgb = [inside[i], inside[i + 1], inside[i + 2]];
      const d = Math.abs(lum(q) - lum(bg));
      if (d > diff) { diff = d; best = q; }
    }
    return { bg, fg: best };
  };

  // Draw an edit on the preview (approximate font, same place and size).
  const paint = (pg: PageInfo, e: Edit) => {
    const ctx = pg.canvas.getContext('2d')!;
    const k = pg.scale * pg.dpr, it = e.item;
    ctx.fillStyle = `rgb(${e.bg.join(',')})`;
    ctx.fillRect(it.x * k - 1.5, (pg.heightPt - it.y - it.h * 0.9) * k, it.w * k + 3, it.h * 1.2 * k);
    ctx.fillStyle = `rgb(${e.fg.join(',')})`;
    ctx.font = `${it.bold ? 'bold ' : ''}${it.h * k}px ${it.family === 'serif' ? 'Times New Roman, serif' : it.family === 'mono' ? 'monospace' : 'Helvetica, Arial, sans-serif'}`;
    const tw = ctx.measureText(e.text).width;
    const x = isNumberish(it.str) ? it.x * k + it.w * k - tw : it.x * k;
    ctx.fillText(e.text, x, (pg.heightPt - it.y) * k);
  };
  // Snapshots of the untouched pages, so edits can be undone.
  const originals = useRef<ImageData[]>([]);
  useEffect(() => {
    if (state !== 'ready') return;
    originals.current = pages.map((pg) => pg.canvas.getContext('2d')!.getImageData(0, 0, pg.canvas.width, pg.canvas.height));
    const box = host.current;
    if (!box) return;
    pages.forEach((pg, i) => {
      const slot = box.querySelector(`[data-page="${i}"]`);
      if (slot && !slot.contains(pg.canvas)) {
        pg.canvas.style.width = '100%'; pg.canvas.style.height = 'auto'; pg.canvas.style.display = 'block';
        slot.prepend(pg.canvas);
      }
    });
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    pages.forEach((pg, i) => {
      const o = originals.current[i];
      if (o) pg.canvas.getContext('2d')!.putImageData(o, 0, 0);
      Object.values(edits).filter((e) => e.item.page === i).forEach((e) => paint(pg, e));
    });
  }, [edits, pages]); // eslint-disable-line react-hooks/exhaustive-deps

  const setEdit = (it: Item, text: string) => {
    setEdits((x) => {
      const n = { ...x };
      if (text === it.str) { delete n[keyOf(it)]; return n; }
      const o = originals.current[it.page];
      // Colours from the untouched page.
      const pg = pages[it.page];
      let col = { bg: [255, 255, 255] as Rgb, fg: [0, 0, 0] as Rgb };
      if (o) {
        const ctx = pg.canvas.getContext('2d')!; const now = ctx.getImageData(0, 0, pg.canvas.width, pg.canvas.height);
        ctx.putImageData(o, 0, 0); col = sample(pg, it); ctx.putImageData(now, 0, 0);
      }
      n[keyOf(it)] = { item: it, text, ...col };
      return n;
    });
  };

  // Apply the chat's changes once the pages are ready: find the text, and when a dish is named ("near"),
  // take the match on the same line as that dish (or the closest one).
  const presetDone = useRef(false);
  useEffect(() => {
    if (state !== 'ready' || !preset?.length || presetDone.current || !originals.current.length) return;
    presetDone.current = true;
    const all = pages.flatMap((pg) => pg.items);
    const missed: string[] = [];
    for (const ch of preset) {
      const f = ch.find.trim().toLowerCase();
      if (!f) continue;
      let hits = all.filter((i) => i.str.toLowerCase().includes(f));
      if (ch.near?.trim()) {
        const nearTxt = ch.near.trim().toLowerCase();
        const anchors = all.filter((i) => i.str.toLowerCase().includes(nearTxt));
        if (anchors.length) {
          const dist = (i: Item) => Math.min(...anchors.filter((a) => a.page === i.page).map((a) => Math.abs(a.y - i.y) * 4 + Math.abs(a.x - i.x) * 0.2), 1e9);
          const best = hits.map((i) => ({ i, d: dist(i) })).sort((a, b) => a.d - b.d)[0];
          hits = best && best.d < 1e9 ? [best.i] : [];
        }
      }
      if (!hits.length) { missed.push(ch.find); continue; }
      const re = new RegExp(ch.find.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      for (const it of hits) setEdit(it, it.str.replace(re, ch.replace));
    }
    setPresetNote(missed.length ? `Couldn't find: ${missed.map((m) => `"${m}"`).join(', ')}. Tap the text on the page to change it.` : 'Changes from your chat are ready. Check them on the page, then save.');
  }, [state, pages, preset]); // eslint-disable-line react-hooks/exhaustive-deps

  const matches = useMemo(() => {
    const f = find.trim().toLowerCase();
    if (!f) return [];
    return pages.flatMap((pg) => pg.items.filter((i) => i.str.toLowerCase().includes(f)));
  }, [find, pages]);
  const replaceAll = () => {
    const f = find.trim();
    if (!f) return;
    const re = new RegExp(f.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    matches.forEach((it) => setEdit(it, it.str.replace(re, repl)));
  };

  const save = async (mode: 'copy' | 'replace') => {
    if (!bytes.current || !Object.keys(edits).length) return;
    if (mode === 'replace' && !window.confirm('Replace the original PDF with this edited one? Everyone sees the new version.')) return;
    setSaving(mode); setErr(null);
    try {
      const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
      const doc = await PDFDocument.load(bytes.current.slice(0));
      const std = {
        sans: [await doc.embedFont(StandardFonts.Helvetica), await doc.embedFont(StandardFonts.HelveticaBold)],
        serif: [await doc.embedFont(StandardFonts.TimesRoman), await doc.embedFont(StandardFonts.TimesRomanBold)],
        mono: [await doc.embedFont(StandardFonts.Courier), await doc.embedFont(StandardFonts.CourierBold)],
      };
      // Letters the standard fonts can't write (e.g. Turkish ş, ğ, ı): use an embedded Unicode font instead.
      let uni: [Awaited<ReturnType<typeof doc.embedFont>>, Awaited<ReturnType<typeof doc.embedFont>>] | null = null;
      const needUni = (t: string) => /[^\u0000-ÿ€–—‘’“”•…]/.test(t);
      if (Object.values(edits).some((e) => needUni(e.text))) {
        const fontkit = (await import('@pdf-lib/fontkit')).default;
        doc.registerFontkit(fontkit);
        const get = (f: string) => fetch(`/fonts/${f}`).then((r) => { if (!r.ok) throw new Error('Could not load the font for these letters.'); return r.arrayBuffer(); });
        uni = [await doc.embedFont(await get('NotoSans-Regular.ttf'), { subset: false }), await doc.embedFont(await get('NotoSans-Bold.ttf'), { subset: false })];
      }
      const c01 = (q: Rgb) => rgb(q[0] / 255, q[1] / 255, q[2] / 255);
      for (const e of Object.values(edits)) {
        const page = doc.getPage(e.item.page);
        const it = e.item;
        const font = (needUni(e.text) && uni ? uni : std[it.family])[it.bold ? 1 : 0];
        page.drawRectangle({ x: it.x - 0.8, y: it.y - it.h * 0.3, width: it.w + 1.6, height: it.h * 1.2, color: c01(e.bg) });
        const tw = font.widthOfTextAtSize(e.text, it.h);
        page.drawText(e.text, { x: isNumberish(it.str) ? it.x + it.w - tw : it.x, y: it.y, size: it.h, font, color: c01(e.fg) });
      }
      const out = await doc.save();
      const base = name.replace(/\.pdf$/i, '');
      const d = new Date();
      const file = new File([out], mode === 'copy' ? `${base} (edited ${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}).pdf` : `${base}.pdf`, { type: 'application/pdf' });
      const e2 = await onSave(file, mode);
      if (e2) { setErr(e2); setSaving(null); return; }
      onClose();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Could not save the PDF.'); setSaving(null); }
  };

  const count = Object.keys(edits).length;
  const input = { background: c.bgElevated, color: c.text, border: `1px solid ${c.border}` };
  const root = document.documentElement.classList.contains('cap-native') ? document.getElementById('root') ?? document.body : document.body;
  return createPortal(
    <div className="fixed inset-0 z-[90] flex flex-col" style={{ background: c.bg }}>
      <header className="flex items-center gap-2 px-4 py-3 flex-wrap" style={{ borderBottom: `1px solid ${c.border}`, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' }}>
        <button onClick={() => { if (!count || window.confirm('Close without saving your edits?')) onClose(); }} className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ color: c.text }} aria-label="Close"><X size={18} /></button>
        <div className="flex-1 min-w-[160px]">
          <p className="text-[15px] font-semibold truncate" style={{ color: c.text }}>Quick edit · {name}</p>
          <p className="text-[12px]" style={{ color: c.textDim }}>Tap any text to change it. Best for prices and small wording; the layout stays as it is.</p>
        </div>
        <button onClick={() => void save('copy')} disabled={!count || !!saving} className="h-9 px-3 rounded-xl text-[13px] font-semibold flex items-center gap-1.5 disabled:opacity-40" style={{ background: c.iconBg, color: c.text }}>
          {saving === 'copy' && <Loader2 size={13} className="animate-spin" />} Save as a copy
        </button>
        <button onClick={() => void save('replace')} disabled={!count || !!saving} className="h-9 px-3 rounded-xl text-[13px] font-semibold flex items-center gap-1.5 disabled:opacity-40" style={{ background: c.accent, color: '#fff' }}>
          {saving === 'replace' ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Replace original
        </button>
      </header>

      <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
        <div ref={host} className="flex-1 min-w-0 overflow-auto p-3" style={{ background: c.iconBg }}>
          {state === 'loading' && <div className="flex justify-center py-20"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div>}
          {state === 'failed' && (
            <div className="text-center py-16 px-6">
              <p className="text-sm" style={{ color: c.danger }}>Couldn't open this PDF for editing.</p>
              {failWhy && <p className="text-[12px] mt-2 break-words" style={{ color: c.textFaint }}>{failWhy}</p>}
            </div>
          )}
          {pages.map((pg, i) => (
            <div key={i} data-page={i} className="relative mx-auto mb-3 shadow-sm" style={{ width, background: '#fff' }}>
              {pg.items.map((it) => {
                const s = pg.scale, k = keyOf(it), on = !!edits[k], picked = sel && keyOf(sel) === k;
                return (
                  <button key={k} onClick={() => { setSel(it); setDraft(edits[k]?.text ?? it.str); }} title={it.str}
                    className="absolute rounded-[2px]"
                    style={{ left: it.x * s - 1, top: (pg.heightPt - it.y - it.h * 0.9) * s - 1, width: it.w * s + 2, height: it.h * 1.2 * s + 2,
                      outline: picked ? `2px solid ${c.accent}` : on ? `1.5px dashed ${c.gold}` : undefined, background: 'transparent' }}
                    onMouseEnter={(e) => { if (!picked && !on) e.currentTarget.style.outline = `1px solid ${c.accent}88`; }}
                    onMouseLeave={(e) => { if (!picked && !on) e.currentTarget.style.outline = ''; }} aria-label={`Edit "${it.str}"`} />
                );
              })}
            </div>
          ))}
        </div>

        <aside className="w-full lg:w-[340px] shrink-0 overflow-y-auto p-4 space-y-5" style={{ borderLeft: `1px solid ${c.border}`, paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}>
          {presetNote && <p className="text-[13px] rounded-xl px-3 py-2" style={{ background: c.accentBg, color: c.text }}>{presetNote}</p>}
          <section className="space-y-2">
            <h3 className="text-[12px] font-bold uppercase tracking-[0.06em] flex items-center gap-1.5" style={{ color: c.textDim }}><Pencil size={12} /> Selected text</h3>
            {sel ? (
              <>
                <p className="text-[12px]" style={{ color: c.textFaint }}>Was: "{sel.str}"</p>
                <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setEdit(sel, draft); } }}
                  className="w-full h-10 rounded-xl px-3 text-[14px]" style={input} />
                <div className="flex gap-2">
                  <button onClick={() => setEdit(sel, draft)} className="h-9 px-3 rounded-xl text-[13px] font-semibold" style={{ background: c.accent, color: '#fff' }}>Apply</button>
                  {edits[keyOf(sel)] && <button onClick={() => { setEdit(sel, sel.str); setDraft(sel.str); }} className="h-9 px-3 rounded-xl text-[13px] font-semibold flex items-center gap-1.5" style={{ background: c.iconBg, color: c.text }}><Undo2 size={13} /> Undo</button>}
                </div>
                {draft.length > sel.str.length + 4 && <p className="text-[12px]" style={{ color: c.gold }}>Much longer than the original: it may run into the text next to it.</p>}
              </>
            ) : <p className="text-[13px]" style={{ color: c.textDim }}>Tap a price or word on the page.</p>}
          </section>

          <section className="space-y-2">
            <h3 className="text-[12px] font-bold uppercase tracking-[0.06em] flex items-center gap-1.5" style={{ color: c.textDim }}><Search size={12} /> Find and replace</h3>
            <input value={find} onChange={(e) => setFind(e.target.value)} placeholder="Find, e.g. 38" className="w-full h-10 rounded-xl px-3 text-[14px]" style={input} />
            <input value={repl} onChange={(e) => setRepl(e.target.value)} placeholder="Replace with, e.g. 40" className="w-full h-10 rounded-xl px-3 text-[14px]" style={input} />
            {find.trim() && <p className="text-[12px]" style={{ color: c.textDim }}>{matches.length} {matches.length === 1 ? 'match' : 'matches'}{matches.length ? `: ${matches.slice(0, 4).map((m) => `"${m.str.trim()}"`).join(', ')}${matches.length > 4 ? '…' : ''}` : ''}</p>}
            <button onClick={replaceAll} disabled={!matches.length} className="h-9 px-3 rounded-xl text-[13px] font-semibold disabled:opacity-40" style={{ background: c.iconBg, color: c.text }}>Replace {matches.length || ''}</button>
          </section>

          <section className="space-y-1.5">
            <h3 className="text-[12px] font-bold uppercase tracking-[0.06em]" style={{ color: c.textDim }}>Changes ({count})</h3>
            {Object.values(edits).map((e) => (
              <button key={keyOf(e.item)} onClick={() => { setSel(e.item); setDraft(e.text); }} className="w-full text-left text-[13px] flex items-center gap-2" style={{ color: c.text }}>
                <span className="truncate flex-1">"{e.item.str.trim()}" → "{e.text.trim()}"</span>
                <span className="text-[11px]" style={{ color: c.textFaint }}>p{e.item.page + 1}</span>
              </button>
            ))}
            {!count && <p className="text-[13px]" style={{ color: c.textFaint }}>No changes yet.</p>}
          </section>
          <p className="text-[11.5px]" style={{ color: c.textFaint }}>The new text uses a standard font close to the original. Adding or removing dishes, or anything that moves the layout, still needs the designer's file.</p>
          {err && <p className="text-sm" style={{ color: c.danger }}>{err}</p>}
        </aside>
      </div>
    </div>,
    root,
  );
}

// page.getTextContent() loops over a stream with `for await`, which older iOS web views can't do.
// If it fails, read the same stream by hand.
type TextBits = { items: unknown[]; styles: Record<string, unknown> };
async function readText(page: { getTextContent: () => Promise<unknown>; streamTextContent: () => ReadableStream<TextBits> }): Promise<{ items: unknown[]; styles: Record<string, unknown> }> {
  try {
    return (await page.getTextContent()) as TextBits;
  } catch {
    const reader = page.streamTextContent().getReader();
    const out: TextBits = { items: [], styles: {} };
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      out.items.push(...value.items);
      Object.assign(out.styles, value.styles);
    }
    return out;
  }
}
